<template>
    <div class="container my-4 pb-5">
        <div class="row justify-content-center mb-4">
            <div class="col-lg-9">
                <div class="d-flex justify-content-end">
                    <a href="/business/events" class="btn btn-link text-decoration-none">
                        <i class="fas fa-arrow-left"></i> Back to Events
                    </a>
                </div>
                <div class="indicator-section mb-4">
                    <h3 class="font-weight-400 mb-4 text-center">{{ stages[stage - 1].name }}</h3>
                    <step-indicator class="mb-4" :steps="stages" :active-step="stage - 1"></step-indicator>
                </div>
                <transition name="shake">
                    <p v-if="message && error"
                       :class="['alert text-center mb-0 rounded my-3 px-3 alert-danger']">
                        {{ message }}
                    </p>
                </transition>
                <transition-group name="slide-in-left">
                    <!-- Step 1: Event Details -->
                    <div key="step1" v-if="stage === 1">
                        <div v-if="stageComplete">
                            <div class="text-center text-white pt-6 mt-6" style="height: 30vw">
                                <transition name="bounce">
                                    <div v-if="message && !error">
                                        <check-mark class="p-6"></check-mark>
                                        <h5 class="text-5">{{ message }}</h5>
                                    </div>
                                </transition>
                            </div>
                        </div>
                        <div v-else>
                            <div class="card card-body border-0">
                                <h6 class="font-weight-bold mb-4">Event Details</h6>
                                <div class="form-group">
                                    <label>Event Image</label>
                                    <file-upload fileType="image"  :croppedImageHeight="350" :aspectRatio="1" v-model="eventForm.image"></file-upload>
                                    <div v-text="eventForm.errors.get('image')" class="text-danger font-12"/>
                                </div>
                                <div class="form-group">
                                    <label>Event Name</label>
                                    <input
                                        type="text"
                                        v-model="eventForm.name"
                                        class="form-control"
                                        placeholder="Enter event name"
                                    />
                                    <div v-text="eventForm.errors.get('name')" class="text-danger font-12"></div>
                                </div>
                                <div class="form-group">
                                    <label>Event Description</label>
                                    <textarea
                                        v-model="eventForm.description"
                                        class="form-control"
                                        placeholder="Enter event description"
                                    ></textarea>
                                    <div v-text="eventForm.errors.get('description')" class="text-danger font-12"></div>
                                </div>
                                <div class="form-group">
                                    <label>Category</label>
                                    <select v-model="eventForm.category" class="form-control">
                                        <option selected value="" disabled>-- Select Category --</option>
                                        <option v-for="c in eventCategories" :key="c" :value="c">{{ c }}</option>
                                    </select>
                                    <div v-text="eventForm.errors.get('category')" class="text-danger font-12"></div>
                                </div>
                                <div class="form-group">
                                    <label>Venue</label>
                                    <input
                                        type="text"
                                        v-model="eventForm.venue"
                                        class="form-control"
                                        placeholder="Enter venue"
                                    />
                                    <div v-text="eventForm.errors.get('venue')" class="text-danger font-12"></div>
                                </div>
                                <div class="form-group">
                                    <label>Date</label>
                                    <input
                                        type="date"
                                        v-model="eventForm.event_at"
                                        class="form-control"
                                    />
                                    <div v-text="eventForm.errors.get('event_at')" class="text-danger font-12"></div>
                                </div>
                                <div class="form-group">
                                    <label>Time</label>
                                    <input
                                        type="time"
                                        v-model="eventForm.time"
                                        class="form-control"
                                    />
                                    <div v-text="eventForm.errors.get('time')" class="text-danger font-12"></div>
                                </div>
                            </div>
                            <button
                                class="btn btn-primary btn-block my-4"
                                :class="{ 'btn-loading': loading }"
                                @click="createEvent"
                            >
                                Next
                            </button>
                        </div>

                    </div>

                    <!-- Step 2: Ticket Types -->
                    <div key="step2" v-if="stage === 2">
                        <div v-if="stageComplete">
                            <div class="text-center text-white pt-6 mt-6" style="height: 30vw">
                                <transition name="bounce">
                                    <div v-if="message && !error">
                                        <check-mark class="p-6"></check-mark>
                                        <h5 class="text-5">{{ message }}</h5>
                                    </div>
                                </transition>
                            </div>
                        </div>
                        <div v-else>
                            <div class="">
                                <h6 class="font-weight-bold mb-4">Create Ticket Types</h6>
                                <div
                                    v-for="(ticket, index) in ticketForm.tickets"
                                    :key="index"
                                    class="ticket-type-group bg-white mb-4"
                                >
                                    <div class="row">
                                        <div class="col-md-6 col-12">
                                            <div class="form-group">
                                                <label>Type Name</label>
                                                <select
                                                    v-model="ticket.name"
                                                    class="form-control"
                                                >
                                                    <option selected disabled>-- Select Ticket Type --</option>
                                                    <option v-for="type in ticketTypes" :key="type">{{ type }}</option>
                                                </select>
                                                <div v-text="ticketForm.errors.get(`tickets.${index}.name`)" class="text-danger font-12"></div>
                                            </div>
                                        </div>
                                        <div class="col-md-6 col-12">
                                            <div class="form-group">
                                                <label>Price (USD)</label>
                                                <input
                                                    type="number"
                                                    v-model="ticket.price"
                                                    class="form-control"
                                                    placeholder="0.00"
                                                />
                                                <div v-text="ticketForm.errors.get(`tickets.${index}.price`)" class="text-danger font-12"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col-12">
                                            <div class="form-group">
                                                <label>Description</label>
                                                <textarea
                                                    v-model="ticket.description"
                                                    class="form-control"
                                                    placeholder="Enter description"
                                                ></textarea>
                                                <div v-text="ticketForm.errors.get(`tickets.${index}.description`)" class="text-danger font-12"></div>
                                            </div>
                                        </div>
                                        <div class="col-12">
                                            <div class="form-group">
                                                <label>Type</label>
                                                <select v-model="ticket.type" class="form-control">
                                                    <option selected value="" disabled>-- Select Type --</option>
                                                    <option value="1-Day Ticket">1-Day Ticket</option>
                                                    <option value="Multi-Day Ticket">Multi-Day Ticket</option>
                                                </select>
                                                <div v-text="ticketForm.errors.get(`tickets.${index}.type`)" class="text-danger font-12"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="ticket.type === 'Multi-Day Ticket'" class="form-group">
                                        <label>Number of Days</label>
                                        <input
                                            type="number"
                                            v-model="ticket.days"
                                            class="form-control"
                                            placeholder="Enter number of days"
                                        />
                                        <div v-text="ticketForm.errors.get(`tickets.${index}.days`)" class="text-danger font-12"></div>
                                    </div>
                                    <div class="row">
                                        <div class="col-md-6 col-12">
                                            <div class="form-group">
                                                <label>Quantity</label>
                                                <input
                                                    type="number"
                                                    v-model="ticket.quantity"
                                                    class="form-control"
                                                    placeholder="Enter ticket quantity"
                                                />
                                                <div v-text="ticketForm.errors.get(`tickets.${index}.quantity`)" class="text-danger font-12"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="text-end">
                                        <button
                                            class="btn btn-danger btn-sm mt-2"
                                            @click="removeTicketType(index)"
                                        >
                                            Remove Ticket Type
                                        </button>
                                    </div>
                                    <hr v-if="index !== ticketForm.tickets.length - 1" />
                                </div>
                                <button
                                    class="btn btn-success btn-block mt-3"
                                    @click="addTicketType"
                                >
                                    Add Another Ticket Type
                                </button>
                            </div>
                            <button
                                class="btn btn-primary btn-block my-4"
                                :class="{ 'btn-loading': loading }"
                                @click="createTicketType"
                            >
                                Next
                            </button>
                        </div>
                    </div>

                    <!-- Step 3: Bank Account Details -->
                    <div key="step3" v-if="stage === 3">
                        <div v-if="stageComplete">
                            <div class="text-center text-white pt-6 mt-6" style="height: 30vw">
                                <transition name="bounce">
                                    <div v-if="message && !error">
                                        <check-mark class="p-6"></check-mark>
                                        <h5 class="text-5">{{ message }}</h5>
                                    </div>
                                </transition>
                            </div>
                        </div>
                        <div v-else>
                            <div class="card card-body border-0">
                                <h6 class="font-weight-bold mb-4">Bank Account Details</h6>
                                <div id="bankDetailsAccordion">
                                    <!-- ZWG Account Details -->
                                    <div class="card">
                                        <div class="card-header" id="zwgHeading">
                                            <h6 class="mb-0">
                                                <button
                                                    class="btn btn-link"
                                                    type="button"
                                                    data-toggle="collapse"
                                                    data-target="#zwgAccount"
                                                    aria-expanded="true"
                                                    aria-controls="zwgAccount"
                                                >
                                                    ZWG Account Details
                                                </button>
                                            </h6>
                                        </div>
                                        <div
                                            id="zwgAccount"
                                            class="collapse show"
                                            aria-labelledby="zwgHeading"
                                            data-parent="#bankDetailsAccordion"
                                        >
                                            <div class="card-body">
                                                <div class="form-group">
                                                    <label>Bank Name</label>
                                                    <input
                                                        type="text"
                                                        v-model="bankForm.zwg_bank_name"
                                                        class="form-control"
                                                        placeholder="Enter bank name"
                                                        list="zwgBanks"
                                                    >
                                                    <datalist id="zwgBanks">
                                                        <option v-for="bank in banks" :key="bank" :value="bank" />
                                                    </datalist>
                                                    <div v-text="bankForm.errors.get('zwg_bank_name')" class="text-danger font-12"></div>
                                                </div>
                                                <div class="form-group">
                                                    <label>Account Name</label>
                                                    <input
                                                        type="text"
                                                        v-model="bankForm.zwg_account_name"
                                                        class="form-control"
                                                        placeholder="Enter account name"
                                                    >
                                                    <div v-text="bankForm.errors.get('zwg_account_name')" class="text-danger font-12"></div>
                                                </div>
                                                <div class="form-group">
                                                    <label>Account Number</label>
                                                    <input
                                                        type="text"
                                                        v-model="bankForm.zwg_account_number"
                                                        class="form-control"
                                                        placeholder="Enter account number"
                                                    >
                                                    <div v-text="bankForm.errors.get('zwg_account_number')" class="text-danger font-12"></div>
                                                </div>
                                                <div class="form-group">
                                                    <label>Branch</label>
                                                    <input
                                                        type="text"
                                                        v-model="bankForm.zwg_branch"
                                                        class="form-control"
                                                        placeholder="Enter branch name"
                                                    >
                                                    <div v-text="bankForm.errors.get('zwg_branch')" class="text-danger font-12"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- USD Account Details -->
                                    <div class="card">
                                        <div class="card-header" id="usdHeading">
                                            <h6 class="mb-0">
                                                <button
                                                    class="btn btn-link collapsed"
                                                    type="button"
                                                    data-toggle="collapse"
                                                    data-target="#usdAccount"
                                                    aria-expanded="false"
                                                    aria-controls="usdAccount"
                                                >
                                                    USD Account Details
                                                </button>
                                            </h6>
                                        </div>
                                        <div
                                            id="usdAccount"
                                            class="collapse"
                                            aria-labelledby="usdHeading"
                                            data-parent="#bankDetailsAccordion"
                                        >
                                            <div class="card-body">
                                                <div class="form-group">
                                                    <label>Bank Name</label>
                                                    <input
                                                        type="text"
                                                        v-model="bankForm.usd_bank_name"
                                                        class="form-control"
                                                        placeholder="Enter bank name"
                                                        list="usdBanks"
                                                    >
                                                    <datalist id="usdBanks">
                                                        <option v-for="bank in banks" :key="bank" :value="bank" />
                                                    </datalist>
                                                    <div v-text="bankForm.errors.get('usd_bank_name')" class="text-danger font-12"></div>
                                                </div>
                                                <div class="form-group">
                                                    <label>Account Name</label>
                                                    <input
                                                        type="text"
                                                        v-model="bankForm.usd_account_name"
                                                        class="form-control"
                                                        placeholder="Enter account name"
                                                    >
                                                    <div v-text="bankForm.errors.get('usd_account_name')" class="text-danger font-12"></div>
                                                </div>
                                                <div class="form-group">
                                                    <label>Account Number</label>
                                                    <input
                                                        type="text"
                                                        v-model="bankForm.usd_account_number"
                                                        class="form-control"
                                                        placeholder="Enter account number"
                                                    >
                                                    <div v-text="bankForm.errors.get('usd_account_number')" class="text-danger font-12"></div>
                                                </div>
                                                <div class="form-group">
                                                    <label>Branch</label>
                                                    <input
                                                        type="text"
                                                        v-model="bankForm.usd_branch"
                                                        class="form-control"
                                                        placeholder="Enter branch name"
                                                    >
                                                    <div v-text="bankForm.errors.get('usd_branch')" class="text-danger font-12"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex justify-content-between my-4">
                                <button
                                    class="btn btn-primary btn-block"
                                    :class="{ 'btn-loading': loading }"
                                    @click="submitBankDetails"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Step 4: Submit for Approval -->
                    <div key="step4" v-if="stage === 4">
                        <div class="card card-body border-0">
                            <h6 class="font-weight-bold mb-4">Submit for Approval</h6>
                            <h6 class="font-weight-bold text-2">Review Your Details</h6>

                            <!-- Event Details -->
                            <h6 class="mt-4">Event Details</h6>
                            <div class="row">
                                <div class="col-12">
                                    <p><strong>Event Name:</strong> {{ eventForm.name }}</p>
                                    <p><strong>Description:</strong> {{ eventForm.description }}</p>
                                    <p><strong>Category:</strong> {{ eventForm.category }}</p>
                                    <p><strong>Venue:</strong> {{ eventForm.venue }}</p>
                                    <p><strong>Date:</strong> {{ eventForm.event_at }}</p>
                                    <p><strong>Time:</strong> {{ eventForm.time }}</p>
                                </div>
                            </div>

                            <!-- Ticket Types -->
                            <h6 class="mt-4">Ticket Types</h6>
                            <table class="table table-bordered">
                                <thead>
                                <tr>
                                    <th>Type Name</th>
                                    <th>Price</th>
                                    <th>Description</th>
                                    <th>Type</th>
                                    <th>Days</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(ticket, index) in ticketForm.tickets" :key="index">
                                    <td>{{ ticket.name }}</td>
                                    <td>${{ ticket.price }}</td>
                                    <td>{{ ticket.description }}</td>
                                    <td>{{ ticket.type }}</td>
                                    <td v-if="ticket.type === 'Multi-Day Ticket'">{{ ticket.days }}</td>
                                </tr>
                                </tbody>
                            </table>

                            <!-- Bank Account Details -->
                            <h6 class="mt-4">Bank Account Details</h6>
                            <table class="table table-bordered">
                                <thead>
                                <tr>
                                    <th>Currency</th>
                                    <th>Bank Name</th>
                                    <th>Account Name</th>
                                    <th>Account Number</th>
                                    <th>Branch</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td>ZWG</td>
                                    <td>{{ bankForm.zwg_bank_name || "Not Provided" }}</td>
                                    <td>{{ bankForm.zwg_account_name || "Not Provided" }}</td>
                                    <td>{{ bankForm.zwg_account_number || "Not Provided" }}</td>
                                    <td>{{ bankForm.zwg_branch || "Not Provided" }}</td>
                                </tr>
                                <tr>
                                    <td>USD</td>
                                    <td>{{ bankForm.usd_bank_name || "Not Provided" }}</td>
                                    <td>{{ bankForm.usd_account_name || "Not Provided" }}</td>
                                    <td>{{ bankForm.usd_account_number || "Not Provided" }}</td>
                                    <td>{{ bankForm.usd_branch || "Not Provided" }}</td>
                                </tr>
                                </tbody>
                            </table>

                            <!-- Terms and Submit Button -->
                            <div class="form-check mt-3">
                                <input
                                    type="checkbox"
                                    class="form-check-input"
                                    id="agreeTerms"
                                    v-model="agreeToTerms"
                                >
                                <label class="form-check-label" for="agreeTerms">
                                    I have reviewed and agree to the <a href="" target="_blank">Terms and Conditions</a>.
                                </label>
                            </div>
                        </div>

                        <div class="d-flex justify-content-between my-4">
                            <button
                                class="btn btn-primary btn-block"
                                :class="{ 'btn-loading': loading }"
                                @click="submitForApproval"
                                :disabled="!agreeToTerms"
                            >
                                Submit for Approval
                            </button>
                        </div>
                    </div>

                </transition-group>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../../core/forms/form";
import StepIndicator from "../../core/step-indicator.vue";
import FileUpload from "../components/FileUpload.vue";
import Checkmark from "../../core/Checkmark.vue";

export default {
    name: "Event",
    components: {CheckMark: Checkmark, FileUpload, StepIndicator },
    data() {
        return {
            stage: 1,
            loading: false,
            agreeToTerms: false,
            stageComplete: false,
            error: false,
            message:'',
            stages: [
                { name: "Create Event" },
                { name: "Add Ticket Types" },
                { name: "Bank Details" },
                { name: "Submit for Approval" },
            ],
            bankForm: new Form({
                event_id: null,
                zwg_bank_name: "",
                zwg_account_name: "",
                zwg_account_number: "",
                zwg_branch: "",
                usd_bank_name: "",
                usd_account_name: "",
                usd_account_number: "",
                usd_branch: "",
            }),
            eventForm: new Form({
                name: "",
                image: "",
                description: "",
                category: "",
                venue: "",
                event_at: "",
                time: "",
            }),
            approvalForm: new Form({
                event_id: null,
            }),
            eventCategories: [
                "Music",
                "Comedy",
                "Theater",
                "Film",
                "Sports",
                "Motorsport",
                "Workshops",
                "Conferences",
                "Festivals",
                "Exhibitions",
                "Community Events",
                "Health and Wellness",
                "Technology",
                "Religious/Spiritual",
                "Corporate",
                "Virtual Events",
                "Private Parties",
                "Food and Drink"
            ],
            ticketTypes: [
                "General Admission",
                "Standard",
                "VIP",
                "VVIP",
                "Early Bird",
                "Student",
                "Child",
                "Senior",
                "Group",
                "Season Pass",
                "Day Pass",
                "Weekend Pass",
                "Reserved Seating",
                "Standing Room",
                "Premium",
                "Backstage Access",
                "Meet and Greet",
                "Livestream Access",
                "All-Access Pass",
                "Corporate Package"
            ],
            ticketForm: new Form({
                event_id: null,
                image: null,
                tickets: [{ name: "", price: "", description: "", type: "", days: 0 , quantity: 0}],
            }),
        };
    },
    mounted() {
        const url = new URL(window.location.href);
        if (!url.searchParams.has("session")) {
            url.searchParams.set("session", "true");
            window.location.href = url.toString();
        }
    },
    methods: {
        goToNextStep() {
            this.stage++;
        },

        addTicketType() {
            this.ticketForm.tickets.push({ name: "", price: "", description: "", type: "", days: 0 });
        },

        removeTicketType(index) {
            this.ticketForm.tickets.splice(index, 1);
        },
        async createEvent() {
            this.loading = true;
            this.error = false;
            this.message = "";
            this.eventForm
                .upload(`/business/event-onboarding/create-event`)
                .then((response) => {
                    this.ticketForm.event_id = response.data.body.model.id;
                    this.ticketForm.image = response.data.body.model.image;
                    this.stageComplete = true;
                    this.message = response.data.message;
                    if(this.stageComplete === true) {
                        setTimeout(function () {
                            this.stageComplete = false;
                            this.stage = 2;
                            this.message = "";
                        }.bind(this), 3000);
                    }
                    this.loading = false;
                })
                .catch((error) => {
                    this.error = true;
                    this.message = error.response?.data?.message || "Failed to create event";
                    this.loading = false;
                });
        },
        async createTicketType() {
            this.loading = true;

            this.ticketForm
                .submit("/business/event-onboarding/ticket-types")
                .then((response) => {
                    this.stageComplete = true;
                    this.message = response.data.message;
                    if(this.stageComplete === true) {
                        setTimeout(function () {
                            this.stageComplete = false;
                            this.stage = 3;
                            this.message = "";
                        }.bind(this), 3000);
                    }
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    console.error("Error submitting ticket details:", error);
                });
        },
        async submitBankDetails() {
            this.loading = true;
            this.error = false;
            this.message = "";

            const zwgFieldsComplete =
                this.bankForm.zwg_bank_name &&
                this.bankForm.zwg_account_name &&
                this.bankForm.zwg_account_number &&
                this.bankForm.zwg_branch;

            const usdFieldsComplete =
                this.bankForm.usd_bank_name &&
                this.bankForm.usd_account_name &&
                this.bankForm.usd_account_number &&
                this.bankForm.usd_branch;

            if (!zwgFieldsComplete || !usdFieldsComplete) {
                this.error = true;
                this.message = "Please complete all fields for ZWG and USD account details.";
                this.loading = false;
                return;
            }
            this.bankForm.event_id = this.ticketForm.event_id;
            this.bankForm
                .submit(`/business/event-onboarding/bank-details`)
                .then((response) => {
                    this.stageComplete = true
                    this.message = response.data.message;
                    if(this.stageComplete === true) {
                        setTimeout(function () {
                            this.stageComplete = false;
                            this.stage = 4;
                            this.message = "";
                        }.bind(this), 3000);
                    }
                    this.loading = false;
                })
                .catch((error) => {
                    this.error = true;
                    this.message = "Please complete all fields for ZWG and USD account details.";
                    this.loading = false;
                });
        },
        async submitForApproval() {
            if (!this.agreeToTerms) {
                this.error = true;
                this.message = "You must agree to the terms and conditions to proceed.";
                return;
            }

            this.loading = true;
            this.error = false;
            this.message = "";


            this.approvalForm.event_id = this.ticketForm.event_id;

            this.approvalForm
                .submit(`/business/event-onboarding/approval`)
                .then((response) => {
                    this.message = response.data.message;
                    this.loading = false;
                    window.location = `${window.location.origin}/business/events`;
                })
                .catch((error) => {
                    this.error = true;
                    this.message = error.response?.data?.message || "Failed to submit for approval.";
                    this.loading = false;
                });
        },
    },
};
</script>
<style scoped>
    .ticket-type-group {
        border: 1px solid #ccc;
        border-radius: 5px;
        padding: 25px;
        margin-bottom: 20px;
        background-color: #f9f9f9;
    }
    .text-danger {
        font-size: 12px;
        color: #dc3545;
    }
</style>

